import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/headerlogo.png";
const pdf = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

export default function TransportationTable18({ data }) {
  const pdfRef = useRef();

  if (!data || data.length === 0) {
    return <p>No data available</p>;
  }

  /* =============================
     PREP DATA
  ============================== */

  const processed = data.map((d) => {
    const female = Number(d.female || 0);
    const male = Number(d.male || 0);
    const total = female + male;
    const femalePercent = total > 0 ? ((female / total) * 100).toFixed(1) : 0;
    const genderGap = male - female;

    return {
      ...d,
      female,
      male,
      total,
      femalePercent,
      genderGap,
    };
  });

  const totalFemale = processed.reduce((sum, r) => sum + r.female, 0);
  const totalMale = processed.reduce((sum, r) => sum + r.male, 0);
  const grandTotal = totalFemale + totalMale;
  const overallFemalePercent =
    grandTotal > 0 ? ((totalFemale / grandTotal) * 100).toFixed(1) : 0;

  /* =============================
     CHART DATA
  ============================== */

  const chartData = {
    labels: processed.map((d) => d.education_label),
    datasets: [
      {
        label: "Female",
        data: processed.map((d) => d.female),
        backgroundColor: "#ec4899",
      },
      {
        label: "Male",
        data: processed.map((d) => d.male),
        backgroundColor: "#3b82f6",
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📊 Employment by Education & Gender (2024)",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return context.parsed.y.toLocaleString("en-US");
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Education Level" },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        title: { display: true, text: "Employment Count" },
        ticks: {
          callback: (value) => value.toLocaleString("en-US"),
        },
      },
    },
  };

  /* =============================
     PDF EXPORT
  ============================== */

  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdfDoc = new jsPDF("l", "mm", "a4");

      const pageWidth = pdfDoc.internal.pageSize.getWidth();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdfDoc.addImage(utahData, "PNG", 20, 10, 32, 16);
      pdfDoc.setFontSize(20);
      pdfDoc.text("UTahData.org", 60, 20);
      pdfDoc.line(10, 30, 287, 30);

      pdfDoc.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);

      pdfDoc.save("education_gender_snapshot_2024.pdf");
    });
  };

  /* =============================
     UI
  ============================== */

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Employment by Education & Gender (2024)
        </h2>

        {/* SUMMARY CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-pink-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Total Female</p>
            <p className="text-xl font-bold text-pink-700">
              {totalFemale.toLocaleString("en-US")}
            </p>
          </div>

          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Total Male</p>
            <p className="text-xl font-bold text-blue-700">
              {totalMale.toLocaleString("en-US")}
            </p>
          </div>

          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Female Share %</p>
            <p className="text-xl font-bold text-green-700">
              {overallFemalePercent}%
            </p>
          </div>
        </div>

        {/* TABLE + CHART */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* TABLE */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm text-center shadow-md rounded-lg overflow-hidden">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Education</th>
                  <th className="p-2">Female</th>
                  <th className="p-2">Male</th>
                  <th className="p-2">Female %</th>
                </tr>
              </thead>
              <tbody>
                {processed.map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-100">
                    <td className="p-2 font-medium">{row.education_label}</td>
                    <td className="p-2 text-pink-700 font-semibold">
                      {row.female.toLocaleString("en-US")}
                    </td>
                    <td className="p-2 text-blue-700 font-semibold">
                      {row.male.toLocaleString("en-US")}
                    </td>
                    <td className="p-2">{row.femalePercent}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* CHART */}
          <div className="w-full h-[450px] bg-white rounded-lg shadow-md p-4">
            <Bar data={chartData} options={options} />
          </div>
        </div>
      </div>

      {/* DOWNLOAD BUTTON */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
